import React, { useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark, faWandMagicSparkles } from "@fortawesome/free-solid-svg-icons";
import { TRIAGE_BUCKET_LABEL } from "../../utils/careTriageModel";
import "./CloseCasePanel.css";

const YES_NO_FIELDS = [
  { key: "physicianContacted", label: "Physician Contacted" },
  { key: "emsInvolved", label: "EMS / 911 Involvement" },
  { key: "erVisit", label: "ER Visit" },
  { key: "hospitalization", label: "Hospitalization" },
  { key: "carePlanModified", label: "Care Plan Modified" },
  { key: "homeCareHoursChanged", label: "Home Care Hours Changed" },
  { key: "familyContacted", label: "Family / Responsible Party Contacted" },
];

const RESOLUTION_STATUSES = [
  "Resolved",
  "Resolved — Ongoing Monitoring",
  "Closed — No Further Action Needed",
  "Escalated / Transferred",
];

function draftResolution(row) {
  return {
    primaryIssue: row?.note || "Change in Aging in Place score or condition signal",
    secondaryIssues: "",
    actionsTaken: row?.assignmentNotes || row?.actionType || "",
    physicianContacted: false,
    emsInvolved: false,
    erVisit: false,
    hospitalization: false,
    carePlanModified: false,
    homeCareHoursChanged: false,
    familyContacted: false,
    equipment: "",
    finalOutcome: "",
    resolutionStatus: "Resolved",
    extraNotes: "",
  };
}

export default function CloseCasePanel({ row, onClose, onConfirm, initialMessage = "" }) {
  const initial = useMemo(() => ({ ...draftResolution(row), ...(row?.resolutionSummary || {}) }), [row]);
  const [form, setForm] = useState(initial);
  const [finalMessage, setFinalMessage] = useState(initialMessage || "Approved and closed.");
  const [saving, setSaving] = useState(false);

  if (!row) return null;

  const bucketLabel = TRIAGE_BUCKET_LABEL[row.bucket] || "Triage";

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleConfirm = async () => {
    setSaving(true);
    try {
      await onConfirm?.({
        resolutionSummary: form,
        outcome: form.finalOutcome || form.primaryIssue,
        finalMessage,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="close-case-backdrop" role="presentation" onClick={onClose}>
      <div
        className="close-case-panel"
        role="dialog"
        aria-labelledby="close-case-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="close-case-panel__top">
          <div>
            <h2 id="close-case-title">Resolution Summary</h2>
            <p className="close-case-panel__sub">
              {row.clientName} · AIP {row.aipCurrent ?? "—"} · {bucketLabel}
            </p>
          </div>
          <button type="button" className="close-case-panel__x" onClick={onClose} aria-label="Close">
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        <p className="close-case-panel__hint">
          <FontAwesomeIcon icon={faWandMagicSparkles} />
          Albert drafted this from the case record. Review, correct, add or remove anything before
          confirming — this becomes the final record used for reporting.
        </p>

        <div className="close-case-panel__body">
          <label>
            Primary Issue
            <input value={form.primaryIssue} onChange={(e) => update("primaryIssue", e.target.value)} />
          </label>
          <label>
            Secondary Issues (if applicable)
            <input value={form.secondaryIssues} onChange={(e) => update("secondaryIssues", e.target.value)} />
          </label>
          <label className="close-case-panel__wide">
            Actions / Interventions Taken
            <textarea rows={2} value={form.actionsTaken} onChange={(e) => update("actionsTaken", e.target.value)} />
          </label>

          <fieldset className="close-case-panel__wide close-case-panel__yesno">
            <legend>Outcome indicators</legend>
            {YES_NO_FIELDS.map(({ key, label }) => (
              <div key={key} className="close-case-panel__yesno-row">
                <span>{label}</span>
                <div className="close-case-panel__toggle" role="radiogroup" aria-label={label}>
                  {[true, false].map((v) => (
                    <button
                      key={String(v)}
                      type="button"
                      role="radio"
                      aria-checked={Boolean(form[key]) === v}
                      className={Boolean(form[key]) === v ? "is-on" : ""}
                      onClick={() => update(key, v)}
                    >
                      {v ? "Yes" : "No"}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </fieldset>

          <label>
            Equipment Ordered / Recommended
            <input value={form.equipment} onChange={(e) => update("equipment", e.target.value)} placeholder="e.g. Grab bars, walker" />
          </label>
          <label>
            Resolution Status
            <select value={form.resolutionStatus} onChange={(e) => update("resolutionStatus", e.target.value)}>
              {RESOLUTION_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
          <label className="close-case-panel__wide">
            Final Outcome
            <textarea rows={2} value={form.finalOutcome} onChange={(e) => update("finalOutcome", e.target.value)} />
          </label>
          <label className="close-case-panel__wide">
            Additional Outcome Information
            <textarea rows={2} value={form.extraNotes} onChange={(e) => update("extraNotes", e.target.value)} />
          </label>
          <label className="close-case-panel__wide">
            Final Review Message
            <input value={finalMessage} onChange={(e) => setFinalMessage(e.target.value)} />
          </label>
        </div>

        <div className="close-case-panel__actions">
          <button type="button" className="close-case-panel__cancel" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="close-case-panel__confirm" disabled={saving} onClick={handleConfirm}>
            {saving ? "Closing…" : "Confirm & Close"}
          </button>
        </div>
      </div>
    </div>
  );
}
